import React, { useEffect, useMemo, useState } from 'react';
import { mockUsers } from '../mockData';
import { auditFetch } from '../utils/auditFetch';
import { APPOINTMENT_STATUSES, getAppointmentStatusLabel, normalizeAppointmentStatus } from '../clinical/appointmentStatus';
import { getTriagePriorityLabel } from '../clinical/preTriage';
import '../styles/Dashboard.css';

const weekdayLabels = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];

const toDatetimeLocal = (date = new Date()) => {
  const next = new Date(date);
  next.setMinutes(next.getMinutes() - next.getTimezoneOffset());
  return next.toISOString().slice(0, 16);
};

const MedicalAgendaPanel = ({ user, mode = 'staff', doctorFilter = '' }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const doctors = mockUsers.doctors;
  const patients = mockUsers.patients;
  const initialDoctor = doctorFilter || doctors[0]?.id || '';
  const [selectedDoctorId, setSelectedDoctorId] = useState(initialDoctor);
  const [selectedPatientId, setSelectedPatientId] = useState(patients[0]?.id || '');
  const [appointments, setAppointments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [message, setMessage] = useState('');
  const [appointmentForm, setAppointmentForm] = useState({
    datetime: toDatetimeLocal(new Date(Date.now() + 60 * 60 * 1000)),
    durationMinutes: 30,
    reason: 'Consulta medica',
  });
  const [scheduleForm, setScheduleForm] = useState({
    weekday: 1,
    startTime: '08:00',
    endTime: '12:00',
    slotMinutes: 30,
  });
  const [blockForm, setBlockForm] = useState({
    startAt: toDatetimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    endAt: toDatetimeLocal(new Date(Date.now() + 25 * 60 * 60 * 1000)),
    reason: 'Bloqueo administrativo',
  });

  const selectedDoctor = useMemo(
    () => doctors.find((doctor) => doctor.id === selectedDoctorId) || doctors[0],
    [doctors, selectedDoctorId]
  );
  const selectedPatient = useMemo(
    () => patients.find((patient) => patient.id === selectedPatientId) || patients[0],
    [patients, selectedPatientId]
  );

  const loadAgenda = () => {
    const doctorQuery = selectedDoctorId ? `doctorId=${encodeURIComponent(selectedDoctorId)}` : '';
    Promise.all([
      auditFetch(`${backendUrl}/api/agenda/appointments${doctorQuery ? `?${doctorQuery}` : ''}`, {}, user)
        .then((res) => res.json())
        .then((data) => data.appointments || []),
      auditFetch(`${backendUrl}/api/agenda/schedules${doctorQuery ? `?${doctorQuery}` : ''}`, {}, user)
        .then((res) => res.json())
        .then((data) => data.schedules || []),
      auditFetch(`${backendUrl}/api/agenda/blocks${doctorQuery ? `?${doctorQuery}` : ''}`, {}, user)
        .then((res) => res.json())
        .then((data) => data.blocks || []),
    ])
      .then(([nextAppointments, nextSchedules, nextBlocks]) => {
        setAppointments(nextAppointments);
        setSchedules(nextSchedules);
        setBlocks(nextBlocks);
      })
      .catch((error) => {
        console.error('Error loading agenda:', error);
        setMessage('No se pudo cargar la agenda.');
      });
  };

  useEffect(() => {
    loadAgenda();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctorId]);

  const createSchedule = async () => {
    const response = await auditFetch(`${backendUrl}/api/agenda/schedules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        doctorId: selectedDoctor.id,
        doctorName: selectedDoctor.name,
        specialty: selectedDoctor.specialty,
        ...scheduleForm,
      }),
    }, user);
    const data = await response.json();
    setMessage(response.ok ? 'Horario medico creado.' : data.message);
    if (response.ok) loadAgenda();
  };

  const createBlock = async () => {
    const response = await auditFetch(`${backendUrl}/api/agenda/blocks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        doctorId: selectedDoctor.id,
        doctorName: selectedDoctor.name,
        specialty: selectedDoctor.specialty,
        startAt: new Date(blockForm.startAt).toISOString(),
        endAt: new Date(blockForm.endAt).toISOString(),
        reason: blockForm.reason,
      }),
    }, user);
    const data = await response.json();
    setMessage(response.ok ? 'Horario bloqueado.' : data.message);
    if (response.ok) loadAgenda();
  };

  const createAppointment = async () => {
    const response = await auditFetch(`${backendUrl}/api/agenda/appointments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        doctor: selectedDoctor,
        patient: selectedPatient,
        datetime: new Date(appointmentForm.datetime).toISOString(),
        durationMinutes: appointmentForm.durationMinutes,
        reason: appointmentForm.reason,
        specialty: selectedDoctor.specialty,
      }),
    }, user);
    const data = await response.json();
    setMessage(response.ok ? 'Cita agendada correctamente.' : data.message);
    if (response.ok) loadAgenda();
  };

  const updateStatus = async (appointment, status) => {
    const response = await auditFetch(`${backendUrl}/api/agenda/appointments/${appointment.id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }, user);
    const data = await response.json();
    setMessage(response.ok ? `Cita ${getAppointmentStatusLabel(status).toLowerCase()}.` : data.message);
    if (response.ok) loadAgenda();
  };

  const reschedule = async (appointment) => {
    const raw = window.prompt('Nueva fecha y hora (YYYY-MM-DDTHH:mm)', toDatetimeLocal(appointment.datetime));
    if (!raw) return;
    const response = await auditFetch(`${backendUrl}/api/agenda/appointments/${appointment.id}/reschedule`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ datetime: new Date(raw).toISOString(), durationMinutes: appointment.durationMinutes || 30 }),
    }, user);
    const data = await response.json();
    setMessage(response.ok ? 'Cita reprogramada.' : data.message);
    if (response.ok) loadAgenda();
  };

  return (
    <div className="agenda-panel">
      <div className="section-header">
        <div>
          <h2>Agenda medica</h2>
          <p>Horarios, bloqueos, pacientes y estados de cita.</p>
        </div>
        <button className="btn btn-secondary" onClick={loadAgenda}>Actualizar</button>
      </div>

      <div className="agenda-grid">
        <label>
          <span>Medico</span>
          <select value={selectedDoctorId} onChange={(event) => setSelectedDoctorId(event.target.value)} disabled={Boolean(doctorFilter)}>
            {doctors.map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.name} · {doctor.specialty}</option>)}
          </select>
        </label>
        <label>
          <span>Especialidad</span>
          <input value={selectedDoctor?.specialty || ''} readOnly />
        </label>
      </div>

      <div className="agenda-management-grid">
        <div className="agenda-box">
          <h3>Crear horario</h3>
          <div className="agenda-grid">
            <select value={scheduleForm.weekday} onChange={(event) => setScheduleForm({ ...scheduleForm, weekday: Number(event.target.value) })}>
              {weekdayLabels.map((label, index) => <option key={label} value={index}>{label}</option>)}
            </select>
            <input type="time" value={scheduleForm.startTime} onChange={(event) => setScheduleForm({ ...scheduleForm, startTime: event.target.value })} />
            <input type="time" value={scheduleForm.endTime} onChange={(event) => setScheduleForm({ ...scheduleForm, endTime: event.target.value })} />
            <input type="number" min="10" value={scheduleForm.slotMinutes} onChange={(event) => setScheduleForm({ ...scheduleForm, slotMinutes: event.target.value })} />
          </div>
          <button className="btn btn-primary full-width" onClick={createSchedule}>Guardar horario</button>
        </div>

        <div className="agenda-box">
          <h3>Bloquear horario</h3>
          <input type="datetime-local" value={blockForm.startAt} onChange={(event) => setBlockForm({ ...blockForm, startAt: event.target.value })} />
          <input type="datetime-local" value={blockForm.endAt} onChange={(event) => setBlockForm({ ...blockForm, endAt: event.target.value })} />
          <input value={blockForm.reason} onChange={(event) => setBlockForm({ ...blockForm, reason: event.target.value })} placeholder="Motivo del bloqueo" />
          <button className="btn btn-secondary full-width" onClick={createBlock}>Bloquear</button>
        </div>

        <div className="agenda-box">
          <h3>Agendar paciente</h3>
          <select value={selectedPatientId} onChange={(event) => setSelectedPatientId(event.target.value)}>
            {patients.map((patient) => <option key={patient.id} value={patient.id}>{patient.name} · {patient.documentId}</option>)}
          </select>
          <input type="datetime-local" value={appointmentForm.datetime} onChange={(event) => setAppointmentForm({ ...appointmentForm, datetime: event.target.value })} />
          <input type="number" min="10" value={appointmentForm.durationMinutes} onChange={(event) => setAppointmentForm({ ...appointmentForm, durationMinutes: event.target.value })} />
          <input value={appointmentForm.reason} onChange={(event) => setAppointmentForm({ ...appointmentForm, reason: event.target.value })} placeholder="Motivo de cita" />
          <button className="btn btn-success full-width" onClick={createAppointment}>Agendar</button>
        </div>
      </div>

      {message && <p className="save-message">{message}</p>}

      <div className="agenda-lists">
        <div className="agenda-box">
          <h3>Horarios configurados</h3>
          {schedules.length === 0 ? <p>No hay horarios.</p> : schedules.map((schedule) => (
            <div className="agenda-list-item" key={schedule.id}>
              <strong>{weekdayLabels[schedule.weekday]}</strong>
              <span>{schedule.startTime} - {schedule.endTime} · {schedule.slotMinutes} min</span>
            </div>
          ))}
        </div>

        <div className="agenda-box">
          <h3>Bloqueos</h3>
          {blocks.length === 0 ? <p>No hay bloqueos.</p> : blocks.map((block) => (
            <div className="agenda-list-item" key={block.id}>
              <strong>{block.reason}</strong>
              <span>{new Date(block.startAt).toLocaleString('es-EC')} - {new Date(block.endAt).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="appointments-grid">
        {appointments.length === 0 ? (
          <div className="empty-state"><p>No hay citas en agenda.</p></div>
        ) : appointments.map((appointment) => (
          <div key={appointment.id} className="appointment-card">
            <div className="apt-header">
              <h3 className="patient-name">{appointment.patient?.name || 'Paciente'}</h3>
              <span className={`status-badge status-${normalizeAppointmentStatus(appointment.status)}`}>{getAppointmentStatusLabel(appointment.status)}</span>
            </div>
            <div className="apt-details">
              <p><strong>Medico:</strong> {appointment.doctor?.name}</p>
              <p><strong>Especialidad:</strong> {appointment.specialty}</p>
              <p><strong>Fecha:</strong> {new Date(appointment.datetime).toLocaleString('es-EC')}</p>
              <p><strong>Motivo:</strong> {appointment.reason}</p>
              <p>
                <strong>Pretriaje:</strong>{' '}
                <span className={`triage-priority triage-priority-${appointment.preTriage?.priority || 'baja'}`}>
                  {appointment.preTriage ? getTriagePriorityLabel(appointment.preTriage.priority) : 'Pendiente'}
                </span>
              </p>
            </div>
            <div className="apt-actions agenda-actions">
              <button className="btn btn-secondary" onClick={() => updateStatus(appointment, APPOINTMENT_STATUSES.confirmed)}>Confirmar</button>
              <button className="btn btn-secondary" onClick={() => reschedule(appointment)}>Reprogramar</button>
              <button className="btn btn-secondary" onClick={() => updateStatus(appointment, APPOINTMENT_STATUSES.noShow)}>No asistio</button>
              <button className="btn btn-danger" onClick={() => updateStatus(appointment, APPOINTMENT_STATUSES.cancelled)}>Cancelar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MedicalAgendaPanel;
